import React, {
    ForwardedRef,
    forwardRef,
    HTMLProps,
    ReactNode,
    useEffect,
    useRef,
    useState,
} from 'react'
import { Color } from '../utils/constants'
import SectionTag from './SectionTag'
import { twMerge } from 'tailwind-merge'
import { useIsMobile } from '../utils/helpers'

export enum Side {
  RIGHT,
  LEFT,
  BOTH,
}

export enum Position {
  TOP,
  CENTER,
  BOTTOM,
}

interface SectionWithTagProps extends HTMLProps<HTMLDivElement> {
  tag: {
    id: string
    color?: Color
    position?: Position
    className?: string
  }
  children: ReactNode
  side?: Side
  resizeCallback?: (ref: HTMLDivElement) => void
  contentClasses?: string
  padding?: { paddingLeft: number; paddingRight?: number }
}

const SectionWithTag = forwardRef(
    (
        {
            tag,
            children,
            side = Side.BOTH,
            resizeCallback,
            className,
            contentClasses,
            padding,
            ...props
        }: SectionWithTagProps,
        ref: ForwardedRef<HTMLDivElement>,
    ) => {
        const { id, color = Color.BLACK, position = Position.TOP, className: tagClassName } = tag
        const tagRef = useRef<HTMLDivElement>(null)
        const [xPadding, setXPadding] = useState(0)
        const [minHeight, setMinHeight] = useState(0)
        const isMobile = useIsMobile()

        useEffect(() => {
            const calculateDimensions = () => {
                requestAnimationFrame(() => {
                    if (tagRef.current && !isMobile) {
                        setXPadding(tagRef.current.clientWidth)
                        setMinHeight(tagRef.current.clientHeight)
                    }
                    if (tagRef.current && resizeCallback) {
                        resizeCallback(tagRef.current)
                    }
                })
            }
            calculateDimensions()
            window.addEventListener('resize', calculateDimensions)
            return () => {
                window.removeEventListener('resize', calculateDimensions)
            }
        }, [tagRef, isMobile, resizeCallback])

        const getPadding = () => {
            const rightPadding = padding?.paddingRight ?? xPadding
            const leftPadding =
        !padding?.paddingLeft || padding.paddingLeft < xPadding ? xPadding : padding.paddingLeft
            switch (side) {
            case Side.BOTH:
            default:
                return `0 ${rightPadding}px 0 ${leftPadding}px`
            case Side.LEFT:
                return `0 0 0 ${leftPadding}px`
            case Side.RIGHT:
                return `0 ${rightPadding}px 0 0`
            }
        }

        const getPosition = () => {
            switch (position) {
            case Position.TOP:
            default:
                return 'items-start'
            case Position.CENTER:
                return 'items-center'
            case Position.BOTTOM:
                return 'items-end'
            }
        }

        return (
            <div className={twMerge('relative', className)} {...props}>
                <div
                    className={twMerge(
                        'md:absolute md:top-0 md:left-0 side-padding flex',
                        getPosition(),
                        tagClassName,
                    )}
                    ref={tagRef}
                >
                    <SectionTag title={id} color={color} />
                </div>
                <div
                    style={{ padding: !isMobile ? getPadding() : undefined, minHeight }}
                    className={twMerge('grid items-center side-padding w-full', contentClasses)}
                    ref={ref}
                >
                    {children}
                </div>
            </div>
        )
    },
)

SectionWithTag.displayName = 'SectionWithTag'
export default SectionWithTag
